import type {
  IntelligenceLiveConnectionPhase,
  IntelligenceLiveConnectionSnapshot,
} from './types';

/**
 * Shared realtime health store for the global connection badge.
 * Pages with no registered monitor stay in `idle`, so v1 falls back to browser online/offline only.
 * Any realtime feature can participate by registering a monitor, marking it `open` or `reconnecting`,
 * and unregistering on cleanup.
 */
type ActiveMonitorPhase = Exclude<IntelligenceLiveConnectionPhase, 'idle'>;
type Listener = () => void;

const listeners = new Set<Listener>();
const monitors = new Map<number, ActiveMonitorPhase>();

let nextMonitorId = 1;
let snapshot: IntelligenceLiveConnectionSnapshot = {
  phase: 'idle',
  monitorCount: 0,
  updatedAt: Date.now(),
};

function getAggregatePhase(): IntelligenceLiveConnectionPhase {
  if (monitors.size === 0) {
    return 'idle';
  }

  const phases = Array.from(monitors.values());

  if (phases.includes('reconnecting')) {
    return 'reconnecting';
  }

  if (phases.every((phase) => phase === 'open')) {
    return 'open';
  }

  return 'connecting';
}

function emitChange() {
  listeners.forEach((listener) => {
    listener();
  });
}

function syncSnapshot() {
  const nextPhase = getAggregatePhase();
  const nextMonitorCount = monitors.size;
  const phaseChanged = nextPhase !== snapshot.phase;
  const countChanged = nextMonitorCount !== snapshot.monitorCount;

  if (!phaseChanged && !countChanged) {
    return;
  }

  snapshot = {
    phase: nextPhase,
    monitorCount: nextMonitorCount,
    updatedAt: phaseChanged ? Date.now() : snapshot.updatedAt,
  };

  emitChange();
}

function updateMonitorPhase(monitorId: number, phase: ActiveMonitorPhase) {
  const currentPhase = monitors.get(monitorId);

  if (!currentPhase || currentPhase === phase) {
    return;
  }

  monitors.set(monitorId, phase);
  syncSnapshot();
}

export function subscribeToIntelligenceLiveConnection(listener: Listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getIntelligenceLiveConnectionSnapshot() {
  return snapshot;
}

export function registerIntelligenceLiveConnectionMonitor() {
  const monitorId = nextMonitorId;
  nextMonitorId += 1;

  monitors.set(monitorId, 'connecting');
  syncSnapshot();

  return monitorId;
}

export function markIntelligenceLiveConnectionOpen(monitorId: number) {
  updateMonitorPhase(monitorId, 'open');
}

export function markIntelligenceLiveConnectionReconnecting(monitorId: number) {
  updateMonitorPhase(monitorId, 'reconnecting');
}

export function unregisterIntelligenceLiveConnectionMonitor(monitorId: number) {
  if (!monitors.delete(monitorId)) {
    return;
  }

  syncSnapshot();
}

export function resetIntelligenceLiveConnectionStore() {
  monitors.clear();
  nextMonitorId = 1;
  snapshot = {
    phase: 'idle',
    monitorCount: 0,
    updatedAt: Date.now(),
  };
  emitChange();
}
